/**
 * Pure helpers used by DiscrepancyReviewDialog. Extracted here so they can
 * be unit-tested without pulling in the full dialog module (Apollo queries,
 * MUI components, etc.).
 *
 * Helpers accept structural types (not the derived GraphQL `Discrepancy`
 * type) so tests can pass minimal fixtures without depending on codegen.
 */
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SyncIcon from "@mui/icons-material/Sync";
import {
  getFieldLabel,
  NULL_PRIOR_UNRESTORABLE_FIELDS,
} from "encamp-shared/src/waste/discrepancyFieldMeta";
import React from "react";

export type DiscrepancyStatus = "unresolved" | "pending" | "resolved" | "ignored";

/**
 * Column-header text treatment shared by the four discrepancy panes: a step
 * smaller than the row text it labels, and bolder.
 *
 * Not the `variant="overline"` treatment the section titles use. Uppercase at
 * 1px tracking made the headers the same size as the body cells (both 14px) and
 * gave them the same treatment as the section title above them, so "DATA SOURCE
 * CONFLICTS" and "WASTE LINE" read at one level. Sentence case is also what the
 * app's own `DataGrid` headers use, which is the grid a reader arrives from.
 *
 * Each pane still sets its own cell's `minWidth`.
 */
export const COLUMN_HEADER_SX = {
  fontSize: "0.8125rem",
  fontWeight: 600,
  lineHeight: 1.5,
} as const;

/** Display order for a status breakdown — unresolved first, terminal statuses last. */
const STATUS_ORDER: readonly DiscrepancyStatus[] = ["unresolved", "pending", "resolved", "ignored"];

/** The minimal discrepancy shape the helpers operate on. */
export type DiscrepancyLike = {
  field: string;
  kind?: string | null;
  resolutionPath?: string | null;
  resolvedBy?: unknown;
  escalation?: string | null;
  status?: string | null;
};

// --- Field label map ---
// Source of truth is encamp-shared; re-exported under the same names so
// existing importers in this app don't need to change.

export { FIELD_LABELS, getFieldLabel } from "encamp-shared/src/waste/discrepancyFieldMeta";

// --- "Update vendor data" eligibility ---

/**
 * Discrepancy fields the "Update vendor data" bulk action cannot apply. These
 * have no single source value to copy into vendor data: structural rows
 * (wasteLinesCount, unpairedWasteLine), compliance rows (eManifestMissing), and
 * the within-line box-13 ↔ box-9 consistency flag (wasteCodeDotConsistency).
 * shippedDate is NOT blocked — the resolver supports it via a partition-aware
 * vendor correction.
 *
 * Mirrors BLOCKED_FIELDS in
 * apps/api/src/resolvers/waste/processedManifestCompliance.ts — the server
 * rejects these too, so they MUST be filtered client-side or the whole bulk
 * action fails.
 */
export const UPDATE_VENDOR_DATA_BLOCKED_FIELDS: ReadonlySet<string> = new Set([
  "eManifestMissing",
  "wasteLinesCount",
  "wasteCodeDotConsistency",
  "unpairedWasteLine",
]);

/** True when a discrepancy field can be resolved via "Update vendor data". */
export function supportsVendorDataUpdate(field: string): boolean {
  return !UPDATE_VENDOR_DATA_BLOCKED_FIELDS.has(field);
}

/** `actorId` for events written by process-manifests rather than a person. */
const SYSTEM_ACTOR_ID = "system";

/**
 * The event fields the undo checks read. Structural, like every other helper
 * here, so a test can pass two plain objects.
 *
 * Ordered oldest first, matching the `seq: "asc"` the events dataloader
 * applies, so the latest event is the last element.
 */
export type UndoEventLike = {
  eventType: string;
  actorId: string;
  vendorValue?: string | null;
};

/**
 * Whether the value the undo would restore is missing or unusable, judged from
 * the row's own history. Null when nothing here blocks the undo.
 *
 * Mirrors the server: `readPriorVendorEvent` reads the prior vendor value off
 * the latest event, and only when that event is a person's RESOLVED. If the
 * latest event is anything else the server refuses for a different reason
 * (recomputed since resolution, resolved automatically), and those refusals
 * stay at click time because the row can change under an open dialog.
 */
function getPriorValueBlock(
  d: Pick<DiscrepancyLike, "field">,
  events: readonly UndoEventLike[]
): string | null {
  const latest = events.at(-1);
  if (!latest || latest.eventType !== "RESOLVED" || latest.actorId === SYSTEM_ACTOR_ID) {
    return null;
  }
  if (latest.vendorValue != null) return null;

  if (NULL_PRIOR_UNRESTORABLE_FIELDS.has(d.field)) {
    return "The previous waste codes were empty, so there is nothing to restore";
  }
  if (d.field === "shippedDate") {
    return "The previous ship date was not recorded, so it cannot be restored";
  }
  return null;
}

/**
 * Whether the "Undo resolution" row action applies, with the text shown as the
 * button's tooltip either way. Undo only serves manual VENDOR_DATA resolutions
 * — the one path that wrote local data. resolvedBy is the manual-vs-automatic
 * discriminator (process-manifests derives resolutionPath for auto-resolved
 * rows too, so the path alone can't tell them apart).
 *
 * `events` is the row's history, oldest first. Pass `undefined` while it is
 * still loading: the button blocks rather than enabling, so it can only ever go
 * from blocked to enabled and never offer an action it then takes away. The
 * four cheap checks run first, so a row refused for its status never waits.
 *
 * This is an earlier copy of the server's checks, not a replacement. The dialog
 * can be stale; `assertUndoSafe` is the guarantee.
 */
export function getUndoResolutionEligibility(
  d:
    | Pick<DiscrepancyLike, "status" | "kind" | "resolutionPath" | "resolvedBy" | "field">
    | undefined,
  events?: readonly UndoEventLike[]
): { enabled: boolean; reason: string } {
  if (d?.status !== "RESOLVED") {
    return { enabled: false, reason: "Only available for resolved discrepancies" };
  }
  if (d.kind === "EPA_CORRECTION_REQUEST") {
    return {
      enabled: false,
      reason: "Tracks an e-Manifest correction request; nothing was written locally",
    };
  }
  if (!d.resolvedBy) {
    return { enabled: false, reason: "Resolved automatically, so there is nothing to restore" };
  }
  if (d.resolutionPath !== "VENDOR_DATA") {
    return { enabled: false, reason: "Nothing was written locally, so there is nothing to undo" };
  }
  if (!events) {
    return { enabled: false, reason: "Checking whether this can be undone" };
  }
  const priorValueBlock = getPriorValueBlock(d, events);
  if (priorValueBlock) return { enabled: false, reason: priorValueBlock };

  return { enabled: true, reason: "Restore the prior vendor value and reopen this discrepancy" };
}

// --- Status derivation ---

/** A discrepancy is a compliance row if it has an escalation level. */
export function isComplianceDiscrepancy(d: { escalation?: string | null }): boolean {
  return d.escalation != null;
}

export function getDiscrepancyStatus(d: { status?: string | null }): DiscrepancyStatus {
  switch (d.status) {
    case "IGNORED":
      return "ignored";
    case "RESOLVED":
      return "resolved";
    case "PENDING":
      return "pending";
    default:
      return "unresolved";
  }
}

/** Matches the subset of MUI Chip `color` values we use for status chips. */
export type StatusChipColor = "warning" | "info" | "success" | "default";

export function getStatusColor(status: DiscrepancyStatus): StatusChipColor {
  switch (status) {
    case "unresolved":
      return "warning";
    case "pending":
      return "info";
    case "resolved":
      return "success";
    case "ignored":
      return "default";
  }
}

export function getStatusLabel(status: DiscrepancyStatus) {
  switch (status) {
    case "unresolved":
      return "Unresolved";
    case "pending":
      return "Pending";
    case "resolved":
      return "Resolved";
    case "ignored":
      return "Non-Discrepancy";
  }
}

// --- Action eligibility (resolve modal) ---
// Shared by DiscrepancyReviewDialog (the selected/single-row targets) and
// ResolveDiscrepanciesDialog (the "apply to others" matching rows) — one
// eligibility rule set for every row a resolve action might touch.

export type ActionKey =
  | "updateVendorData"
  | "resolveExternally"
  | "nonDiscrepancy"
  | "cantDetermine";

export type SkipGroup = {
  reason: string;
  count: number;
};

export type ActionEligibility = {
  eligibleIds: string[];
  skips: SkipGroup[];
};

/** A row `computeActionEligibility` can evaluate — narrower than the full generated Discrepancy type. */
export type EligibilityRow = DiscrepancyLike & { id: string };

export function computeActionEligibility(
  action: ActionKey,
  selected: readonly EligibilityRow[]
): ActionEligibility {
  const eligibleIds: string[] = [];
  const skipCounts = new Map<string, number>();
  const addSkip = (reason: string) => {
    skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);
  };

  // "Can't Resolve — Mark as Blocked" blocks the whole manifest rather than acting on rows,
  // so every selected row is "eligible" — eligibility gating doesn't apply.
  if (action === "cantDetermine") {
    return { eligibleIds: selected.map((d) => d.id), skips: [] };
  }

  for (const d of selected) {
    if (action === "updateVendorData") {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status !== "UNRESOLVED") {
        addSkip("already resolved or pending");
      } else if (!supportsVendorDataUpdate(d.field)) {
        addSkip(`${getFieldLabel(d.field)} doesn't support vendor data update`);
      } else {
        eligibleIds.push(d.id);
      }
    } else if (action === "resolveExternally") {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (d.status === "PENDING") {
        addSkip("already pending");
      } else {
        eligibleIds.push(d.id);
      }
    } else {
      if (d.status === "IGNORED") {
        addSkip("already a non-discrepancy");
      } else if (d.status === "RESOLVED") {
        addSkip("already resolved");
      } else if (isComplianceDiscrepancy(d)) {
        addSkip("compliance discrepancy — use Resolve in e-Manifest instead");
      } else {
        eligibleIds.push(d.id);
      }
    }
  }

  const skips = [...skipCounts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);

  return { eligibleIds, skips };
}

/**
 * Whether the resolve dialog has any action to offer for one row. Mirrors how
 * the dialog builds its action list, so a Resolve control and the dialog it
 * opens cannot disagree about what is possible: a resolved or dismissed row
 * would otherwise open a dialog with every action skipped.
 */
export function hasResolvableAction(row: EligibilityRow, includeVendorAction: boolean): boolean {
  const actions: ActionKey[] = includeVendorAction
    ? ["updateVendorData", "resolveExternally", "nonDiscrepancy"]
    : ["resolveExternally", "nonDiscrepancy"];
  return actions.some((action) => computeActionEligibility(action, [row]).eligibleIds.length > 0);
}

export function formatSkipSummary(skips: SkipGroup[]): string {
  return skips.map((s) => `• ${s.count} ${s.reason}`).join("\n");
}

// --- Status chip iconography ---

/**
 * Returns the icon element to render inside the status chip, or `undefined`
 * for a plain (icon-less) chip.
 */
export function getStatusChipIcon(
  d: Pick<DiscrepancyLike, "field" | "kind" | "resolutionPath" | "resolvedBy">,
  status: DiscrepancyStatus
): React.ReactElement<{ "aria-hidden"?: boolean }> | undefined {
  // eManifestMissing rows show the missing-manifest icon only when active (unresolved/pending).
  // Terminal rows fall through to the generic status-based icons.
  if (d.field === "eManifestMissing" && (status === "unresolved" || status === "pending")) {
    return <ErrorOutlineIcon />;
  }
  // Dismissed as a non-discrepancy (terminal, user-driven).
  if (status === "ignored") return <BlockIcon />;
  // Manual vendor-data fix (a user resolved it locally).
  if (d.resolutionPath === "VENDOR_DATA" && d.resolvedBy) return <CheckIcon />;
  // Resolved at the source / auto-resolved.
  if (status === "resolved") return <PublishedWithChangesIcon />;
  if (status === "pending") return <SyncIcon />;
  return undefined;
}

/**
 * Tooltip text that explains *why* a chip has its current status/icon.
 * The icon alone can't communicate whether a resolved chip was manually
 * refreshed vs auto-resolved by process-manifests, or whether a pending chip is
 * waiting on new vendor data vs an exception report, so surface the detail
 * on hover.
 */
export function getStatusChipTooltip(
  d: Pick<DiscrepancyLike, "field" | "kind" | "resolutionPath" | "resolvedBy" | "escalation">,
  status: DiscrepancyStatus
): string | undefined {
  // eManifestMissing rows show the missing-manifest message only when active (unresolved/pending).
  // Terminal rows fall through to the generic status-based tooltips.
  if (d.field === "eManifestMissing" && (status === "unresolved" || status === "pending")) {
    return "No matching e-Manifest record found for this manifest";
  }

  if (status === "ignored") {
    // resolvedBy present = a person dismissed it; absent = process-manifests
    // auto-ignored a mismatch that converged externally with no staff action.
    return d.resolvedBy
      ? "Marked as a non-discrepancy"
      : "Auto-ignored — data changed externally without staff action";
  }

  if (status === "resolved") {
    if (d.kind === "EPA_CORRECTION_REQUEST") {
      return "Corrected in e-Manifest";
    }
    if (d.resolutionPath === "EMANIFEST") {
      return "Corrected in e-Manifest";
    }
    if (d.resolutionPath === "VENDOR_DATA") {
      return "Vendor data corrected";
    }
    return "Auto-resolved — values now match";
  }

  if (status === "pending") {
    if (d.escalation === "OVERDUE_60") return "Pending exception report";
    // Data could flow back from either side (vendor re-sync or eManifest
    // update in RCRAInfo) — stay source-agnostic.
    return "Pending data refresh";
  }

  if (status === "unresolved") {
    return "Needs review";
  }

  return undefined;
}

// --- Status breakdown & terminal-last ordering ---
// The status filter that used to hide resolved/ignored rows is gone — every
// finding shows regardless of status. These two helpers keep a mixed-status
// list legible without it: one summarizes a section's mix in its header, the
// other keeps a group's actionable rows from getting lost among ones that
// don't need a decision.

/**
 * A section's status mix as a header label, e.g. `"3 unresolved · 1 pending"`,
 * counting only non-zero statuses in unresolved/pending/resolved/ignored
 * order. Collapses to the plain count when only one status is present.
 */
export function getStatusBreakdownLabel(items: readonly { status: DiscrepancyStatus }[]): string {
  const counts = new Map<DiscrepancyStatus, number>();
  for (const item of items) {
    counts.set(item.status, (counts.get(item.status) ?? 0) + 1);
  }
  const present = STATUS_ORDER.filter((status) => (counts.get(status) ?? 0) > 0);
  if (present.length <= 1) return String(items.length);
  return present
    .map((status) => `${counts.get(status)} ${getStatusLabel(status).toLowerCase()}`)
    .join(" · ");
}

/** True for a status that needs no further review action. */
function isTerminalStatus(item: { status?: string | null }): boolean {
  const status = getDiscrepancyStatus(item);
  return status === "resolved" || status === "ignored";
}

/**
 * Stable-sorts rows so unresolved/pending rows come before resolved/ignored
 * ones, preserving each half's existing (server) order. Reason: with the
 * status filter gone, a group's actionable rows would otherwise be scattered
 * among rows needing no decision.
 */
export function sortTerminalLast<T extends { status?: string | null }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => {
    const aTerminal = isTerminalStatus(a);
    const bTerminal = isTerminalStatus(b);
    if (aTerminal === bTerminal) return 0;
    return aTerminal ? 1 : -1;
  });
}

// --- Header context line ---

export interface ManifestContextFields {
  generatorName?: string | null;
  epaId?: string | null;
  tsdfName?: string | null;
  shippedDate?: string | null;
}

/**
 * The dialog subtitle naming the shipment: generator + its EPA id, the
 * receiving facility, and the ship date. Omits any missing segment and its
 * neighboring separator; returns null when nothing is available.
 */
export function formatManifestContextLine(fields: ManifestContextFields): string | null {
  const from = [fields.generatorName, fields.epaId].filter(Boolean).join(" · ");
  const to = fields.tsdfName ?? "";
  let route = "";
  if (from && to) {
    route = `${from} → ${to}`;
  } else {
    route = from || to;
  }
  const shipped = fields.shippedDate ? `shipped ${formatDateOnly(fields.shippedDate)}` : "";
  const line = [route, shipped].filter(Boolean).join(" · ");
  return line || null;
}

// --- Value formatting ---

/** Extract just the date portion (YYYY-MM-DD) from an ISO string. */
export function formatDateOnly(value: string | null | undefined): string {
  if (value == null) return "—";
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : value;
}

/**
 * Strip JSON-ish quoting/bracket artifacts from a stored string value so it
 * renders cleanly. Stored values sometimes look like `"D001"` or
 * `["D001", "D002"]`; we want `D001` and `D001, D002` respectively.
 * `null` and `"[]"` render as an em dash placeholder.
 */
export function formatDisplayValue(value: string | null | undefined): string {
  if (value == null) return "—";
  if (value === "[]") return "—";
  const stripped = value.replace(/^"(.*)"$/, "$1").replace(/"\s*,\s*"/g, ", ");
  const unbracketed = stripped.replace(/^\[(.*)\]$/, "$1").replace(/"/g, "");
  return unbracketed || "—";
}

/**
 * Determines which badge case applies to a field discrepancy row based on
 * which sources differ from the eManifest value.
 *
 * - `"vendor-only"`: only the vendor value differs
 * - `"doc-only"`: only the document value differs
 * - `"both"`: vendor and document both differ (worst case)
 * - `null`: no displayable badge (neither present or both match)
 */
export type DiscrepancyBadgeCase = "vendor-only" | "doc-only" | "both" | null;

export function getDiscrepancyBadgeCase(row: {
  eManifestValue?: string | null;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
  vendorDiscrepant?: boolean | null;
  documentDiscrepant?: boolean | null;
}): DiscrepancyBadgeCase {
  // Prefer the persisted per-axis verdicts (computed by the normalization-aware
  // comparators). Fall back to raw value comparison only for legacy rows that
  // predate those columns (verdict === null), where raw inequality is the best
  // signal available until the row is next recomputed. Raw comparison ignores
  // field-specific normalization (zip, EPA-ID name match, case), so it can show
  // a spurious chip — the verdict columns exist precisely to stop that.
  const vendorDiffers =
    row.vendorDiscrepant ?? (row.vendorValue != null && row.vendorValue !== row.eManifestValue);
  const docDiffers =
    row.documentDiscrepant ??
    (row.manifestDocumentValue != null && row.manifestDocumentValue !== row.eManifestValue);
  if (vendorDiffers && docDiffers) return "both";
  if (vendorDiffers) return "vendor-only";
  if (docDiffers) return "doc-only";
  return null;
}
